/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import { getCatalogId, IComponentProps, ICoreContext, Image, getUrlSync, IImageSettings } from '@msdyn365-commerce/core';
import { ImageFetchPriority } from '@msdyn365-commerce/core-internal';
import {
    getProductPageUrlSync, ProductInput,
    GetCustomerInput, getCustomer, buildWishlistLine, getWishlistByCustomerId,
    OrderTemplate, getFallbackImageUrl
} from '@msdyn365-commerce-modules/retail-actions';
import { getCartStateData, GetCartStateDataInput, ICartState } from '@msdyn365-commerce/global-state';
import { format, Tooltip } from '@msdyn365-commerce-modules/utilities';
import React from 'react';
import { reaction } from 'mobx';
import ProductListOrderQuantity from '../common/product-list-order-quantity';
import { Button, ButtonProps } from 'reactstrap';
import classnames from 'classnames';
import { toast, IToastViewProps, toasts } from '../toast/toast';
import { ProductListProduct, IActionsAvailable, IProductListResources, BuyValidationResult } from './product-list-component';
import { AsyncResult, CommerceList, Customer, ProductList, SimpleProduct, UnitOfMeasureConversion } from '@msdyn365-commerce/retail-proxy';
import { addLinesAsync, createCommerceListAsync, createGetByCustomerInput, removeLinesAsync } from '@msdyn365-commerce/retail-proxy/dist/DataActions/CommerceListsDataActions.g';
import { IWishlistItemsProps } from '../../themes/dlvry-adventureworks/definition-extensions/wishlist-items.ext.props.autogenerated';
import { IWishlistItemsData } from '@msdyn365-commerce-modules/wishlist/src/modules/wishlist-items/wishlist-items.data';
import { AllergensModal } from './components/allergens-modal';
import { ProductInformationModal } from './components/product-information-modal';
import { getLocale } from './locale';
import {
    generateAddToOrderTemplateDialogResources,
    generateOrderTemplateNameDialogResources,
    generateItemAddedToOrderTemplateDialogResources,
    generateDuplicateItemsWhenAddingToOrderTemplateDialogResources
} from './locale/add-to-order-template-resources';
import AddToOrderTemplateComponent from '../../themes/dlvry-adventureworks/views/components/addtoordertemplate.component';
import { removeProductListLinesAsync } from '@msdyn365-commerce/retail-proxy/dist/DataActions/ProductListsDataActions.g';
import { IOrderTemplateViewProps } from '../../modules/dlvry-order-template/dlvry-order-template';
import { ActiveOrderTemplateLinesInput } from '../../modules/dlvry-order-template/actions/get-dlvry-order-template';
import { validateAlcoholLicenseAsync } from '../../actions/validate-alcohol-license.action';
import PQueue from 'p-queue';
import PriceComponent from '../../themes/dlvry-adventureworks/views/components/price.component';
import { UnitOfMeasure } from '../unit-of-measure';
import { getKilogramConversion, getPcsConversion, getRoundedFactor } from '../../shared-utils/unit-conversion-utils';
import DiscountBadge from '../discount-badge/discount-badge';
import { ICartViewProps } from '../../modules/dlvry-cart/dlvry-cart';
import { getTemperatureZoneIcon } from '../../shared-utils/attributes/get-temperature-zone';

/**
 * Data required to render each order history row.
 */
export interface IProductListRowData {
    cart?: ICartState;
    product: ProductListProduct;
    simpleProduct: SimpleProduct | undefined;
    orderTemplate?: OrderTemplate;
    customerInformation?: Customer;
    unitConversions?: UnitOfMeasureConversion[];
    wishlists?: CommerceList[];
}

/**
 * Configuration for the order history rows.
 */
export interface IProductListRowProps extends IComponentProps<IProductListRowData> {
    context: ICoreContext;
    className: string;
    cartQueue: PQueue;
    actionsAvailable?: IActionsAvailable;
    resources: IProductListResources;
    cartViewProps?: ICartViewProps | undefined;
    wishlistItemsProps?: IWishlistItemsProps<IWishlistItemsData> | undefined;
    orderTemplateViewProps?: IOrderTemplateViewProps;
    defaultWishlistName?: string;
    productComparisonButton?: {} | null | undefined;
    moduleId: string;
    moduleType: string;
    error?: string;
    allowMultiSelect?: boolean;
    min?: number;
    isDragOver?: 'over' | 'under' | undefined;
    isSelected?: boolean;
    readOnly?: boolean;
    disableBuyButton?: boolean;
    disableReorder?: boolean;
    disableQuantityInput?: boolean;
    draggable?: boolean;
    lazyLoadImages?: boolean;
    showAvailability?: boolean;
    showPrices?: boolean;
    showTotalPrices?: boolean;
    showCartCount?: boolean;
    userHasAccess?: boolean;
    selectOnQuantityChange?: boolean;
    initialBreakoffEnabled?: boolean;
    buyAlwaysAdd?: boolean;
    resetSelectedQuantities?: boolean;
    retrieveOrderTemplates?(): Promise<OrderTemplate[]>;
    onSelectCartHandler?: () => void;
    onUpdateOrderTemplates?: (orderTemplate?: ProductList) => Promise<OrderTemplate[]>;
    onUpdateQuantity?: (quantity: number) => void;
    onBreakoffChange?: (product: ProductListProduct, enabled: boolean) => void;
    onDrop?: (event: React.DragEvent<HTMLLIElement>, recordId: number | undefined, overUnder: 'over' | 'under' | undefined) => void;
    onDragOver?: (recordId: number | undefined, overUnder: 'over' | 'under' | undefined) => void;
    onMove?: (recordId: number | undefined, direction: 'up' | 'down') => void;
    onBuyValidate?: (product: ProductListProduct, quantity: number) => Promise<BuyValidationResult>;
}

export interface IProductListRowState {
    orderQuantity: number;
    allergensModal: boolean;
    productInformationModal: boolean;
    working: boolean;
    showCartCountTooltip: boolean;
}

/**
 * Renders rows for desktop view.
 * @param props - Configuration for the rows.
 * @returns A react node with a list of order history rows for desktop view.
 */
export class ProductListRow extends React.Component<IProductListRowProps, IProductListRowState> {

    private readonly locale = getLocale(this.props.context);
    private readonly cartUrl = getUrlSync('cart', this.props.context.actionContext);

    public constructor(props: IProductListRowProps) {
        super(props);
        this.state = {
            orderQuantity: props.buyAlwaysAdd
                ? 0
                : props.data.product.CurrentQuantity ?? 0,
            allergensModal: false,
            productInformationModal: false,
            working: false,
            showCartCountTooltip: false
        };
    }

    public componentDidMount(): void {
        reaction(
            () => this.props.data.cart?.cart,
            () => {
                // Force update to fix cart count lagging behind
                this.forceUpdate();
            }
        );

        if (this.props.buyAlwaysAdd) {
            return;
        }

        reaction(
            () => this.props.data.cart?.cart.CartLines,
            () => {
                const { cart, product } = this.props.data;

                if ((product.CartLine?.Quantity ?? 0) > 0 && !cart?.cart.CartLines?.find(x => x.ProductId === product?.RecordId)) {
                    // This product was removed from cart, reset quantity
                    this.setState({
                        orderQuantity: 0
                    });
                }
            }
        );
    }

    private renderProductComparisonButton(
        productComparisonButton: React.ReactNode,
        product: any,
        catalogId: number
    ): JSX.Element | undefined {
        return React.cloneElement(productComparisonButton as React.ReactElement, { product, catalogId });
    }

    public componentDidUpdate(prevProps: Readonly<IProductListRowProps>, prevState: Readonly<IProductListRowState>, snapshot?: any): void {
        if (this.props.buyAlwaysAdd) {
            if (this.props.isSelected && this.props.resetSelectedQuantities) {
                this.setState({ orderQuantity: 0 });
            }
            return;
        }

        if (this.props.data.product.CurrentQuantity !== prevProps.data.product.CurrentQuantity) {
            this.setState({
                orderQuantity: this.props.data.product.CurrentQuantity ?? this.state.orderQuantity
            });
        }
    }

    public render(): JSX.Element | undefined {
        const {
            className,
            data: { product },
            actionsAvailable,
            allowMultiSelect,
            draggable,
            readOnly,
            showAvailability,
            showPrices,
            onUpdateOrderTemplates
        } = this.props;
        const { PrimaryImageUrl } = product;
        const productUrl = product ? getProductPageUrlSync(product.Name ?? '', product.RecordId!, this.props.context.actionContext, undefined) : '';
        const isAuthenticated = this.props.context.request.user.isAuthenticated;
        const price = showPrices !== false
            ? (product.Price && product.Price !== 0.00)
                ? product.Price : undefined
            : undefined;
        const availabilityClass = this._getAvailabilityClassName();
        const availabilityText = availabilityClass === 'error' ? product.Availability?.StockLevelLabel || 'ERROR' : product.Availability?.StockLevelLabel || '';

        const conversions = this.props.data.unitConversions?.filter(x => x.FromUnitOfMeasureSymbol === this.props.data.product?.DefaultUnitOfMeasure);
        const kilogramConversion = conversions && getKilogramConversion(this.props.data.product?.DefaultUnitOfMeasure, conversions);
        const pcsConversion = conversions && getPcsConversion(conversions);
        const temperatureZoneIcon = product.TemperatureZone ? getTemperatureZoneIcon(this.props.context, product.TemperatureZone) : undefined;

        return (
            <li
                key={`${product.RecordId}-${product.DefaultUnitOfMeasure}`}
                data-product-id={product.RecordId}
                className={classnames({
                    'drag-over': this.props.isDragOver,
                    [this.props.isDragOver || '']: this.props.isDragOver
                })}
                onDrop={(e) => {
                    e.stopPropagation(); // Prevent onDrop handler on parent from firing
                    this.props.onDrop?.(e, product.RecordId, this.props.isDragOver);
                }}
                onDragOver={(e) => {
                    e.stopPropagation();

                    if (!this.props.disableReorder && e.dataTransfer.types.includes('product')) {
                        e.preventDefault();

                        const rect = (e.currentTarget as Element).getBoundingClientRect();
                        const y = e.clientY - rect.top;
                        const halfHeight = rect.height / 2;
                        const overOrUnder: 'over' | 'under' = y > halfHeight ? 'under' : 'over';

                        this.props.onDragOver?.(product.RecordId, overOrUnder);
                    }
                }}
            >
                {draggable && (
                    <div
                        className='drag-area'
                        draggable={draggable}
                        onDragStart={draggable ? this._handleDragStart : undefined}
                        onDragEnd={() => this.props.onDragOver?.(undefined, undefined)}
                        onDragOver={draggable ? (e) => {
                            e.stopPropagation();
                            e.preventDefault();
                        } : undefined} />
                )}
                {isAuthenticated && allowMultiSelect && (
                    <label className='checkbox-container'>
                        <input
                            aria-label='selectOneRowText'
                            className='checkbox-input'
                            type='checkbox'
                            checked={this.props.isSelected}
                            aria-checked={this.props.isSelected}
                            onChange={this.props.onSelectCartHandler}
                        />
                        <span className='checkmark'/>
                    </label>
                )}
                <div className={`${className}__product-image`}>
                    <div className={`${className}__image-action-container`}>
                        <a className={`${className}__image-action-container__product-image`} href={productUrl}>
                            {this._renderProductImage(
                                'product-image',
                                this.props.context,
                                product.Name || '',
                                PrimaryImageUrl,
                                undefined,
                                this.props.lazyLoadImages
                            )}
                        </a>
                        {isAuthenticated && (
                            <div className={`${className}__image-action-container__actions`}>
                                {this.state.allergensModal && (
                                    <AllergensModal
                                        className='allergens-modal'
                                        onClose={() => this.setState({ allergensModal: false })}
                                        context={this.props.context}
                                        product={product} />
                                )}
                                <button
                                    className={`${className}__image-action-container__actions__allergens-button`}
                                    title={this.locale.allergens}
                                    aria-label={this.locale.allergens}
                                    onClick={async () => {
                                        this.setState({ allergensModal: true });
                                    }
                                } />

                                <AddToOrderTemplateComponent
                                    className='order-template-button'
                                    addToOrderTemplateButtonText={this.locale.addToOrderTemplate.addToOrderTemplateButtonText || 'addToOrderTemplateButtonText'}
                                    addToOrderTemplateButtonTooltip={this.locale.addToOrderTemplate.addToOrderTemplateButtonTooltip || 'addToOrderTemplateButtonTooltip'}
                                    addToOrderTemplateDialogResources={generateAddToOrderTemplateDialogResources(this.locale)}
                                    createOrderTemplateDialogResources={generateOrderTemplateNameDialogResources(this.locale)}
                                    itemAddedToOrderTemplateDialogResources={generateItemAddedToOrderTemplateDialogResources(this.locale)}
                                    duplicateItemsWhenAddingToOrderTemplateDialogResources={generateDuplicateItemsWhenAddingToOrderTemplateDialogResources(this.locale)}
                                    context={this.props.context}
                                    id={this.props.moduleId}
                                    typeName={this.props.moduleType}
                                    disabled={!this.props.data.simpleProduct || !this.props.data.customerInformation}
                                    disableButton={!this.props.data.simpleProduct || !this.props.data.customerInformation}
                                    data={{
                                        product: this.props.data.simpleProduct || {} as any,
                                        quantity: this.state.orderQuantity || 1,
                                        customerInformation: this.props.data.customerInformation || { IsB2b: true } as any // Always pass 'IsB2b' so that it will always render (unless false from api)
                                    }}
                                    showButtonTooltip={false}
                                    onSuccess={async (orderTemplate) => {
                                        await onUpdateOrderTemplates?.(orderTemplate);
                                        toast({
                                            text: `${this.props.data.product.Name}${this.locale.addToOrderTemplate.itemAddedToOrderTemplateHeaderMessageText}`,
                                            autoClose: 3000,
                                            canClose: true,
                                            position: 'bottom-right',
                                            type: 'success',
                                            showProgress: true
                                        });
                                    }}
                                    retrieveOrderTemplates={this.props.retrieveOrderTemplates}
                                />

                                {this.state.productInformationModal && (
                                    <ProductInformationModal
                                        className='product-information-modal'
                                        onClose={() => this.setState({ productInformationModal: false })}
                                        context={this.props.context}
                                        product={product} />
                                )}
                                <button
                                    className={`${className}__image-action-container__actions__product-information-button`}
                                    title={this.locale.productInformationTitle}
                                    aria-label={this.locale.productInformationTitle}
                                    onClick={() => {
                                        this.setState({ productInformationModal: true });
                                    }
                                } />

                                {actionsAvailable?.addToWishlistAction && this._getWishlistActionButton()}
                            </div>
                        )}

                        {product.Badge && (
                            <DiscountBadge
                                context={this.props.context}
                                badge={product.Badge}
                                size='small' />
                        )}
                    </div>
                </div>

                <div className={`${className}__product-wrapper`}>
                    <div className={`${className}__product`}>
                        <div className={`${className}__product__name-wrapper`}>
                            <a className={`${className}__product__name`} href={productUrl}>
                                {product.Name && this.props.error && (
                                    <span className={`${className}__product__warning`} title={this.props.error || 'Test'}>!</span>
                                )}
                                {product.Transit && (
                                    <div className={`${className}__product__transit-mark`} title={this.props.resources.transitTooltip || 'Transittvare'}>T</div>
                                )}
                                {temperatureZoneIcon && (
                                    <span
                                        className={`${className}__product__temperature-icon ${temperatureZoneIcon}`}
                                        title={product.TemperatureZone} />
                                )}
                                <span style={{ verticalAlign: 'text-top' }}>{product.Name}</span>
                            </a>
                        </div>
                        <div className={`${className}__product__id`}>
                            {product.ItemId}
                        </div>
                        {product.Brand && (
                            <div className={`${className}__product__brand`}>{product.Brand}</div>
                        )}
                        {product.Manufacturer && (
                            <div className={`${className}__product__manufacturer`}>{product.Manufacturer}</div>
                        )}
                        {showAvailability && (
                            <div className={`${className}__product__availability`}>
                                <i className={availabilityClass}>
                                    {availabilityText}
                                </i>
                            </div>
                        )}
                    </div>
                    <div className={classnames(`${className}__product__price`, { 'add-margin': isAuthenticated && actionsAvailable?.removeAction })}>
                        {isAuthenticated && (
                            <>
                            <div className={`${className}__price`}>
                                {this._renderPrice(className, price)}
                            </div>

                            {price && kilogramConversion && getRoundedFactor(kilogramConversion) > 0 && (
                                <div className={`${className}__price__extra`}>
                                    <PriceComponent
                                        context={this.props.context}
                                        data={{ price: { CustomerContextualPrice: price / getRoundedFactor(kilogramConversion) } }}
                                        id={this.props.id}
                                        typeName={this.props.typeName} />
                                    &nbsp;
                                    pr. kg
                                </div>
                            )}

                            {price && pcsConversion && (
                                <div className={`${className}__price__extra`}>
                                    <PriceComponent
                                            context={this.props.context}
                                            data={{ price: { CustomerContextualPrice: price / (pcsConversion.Factor || 1) } }}
                                            id={this.props.id}
                                            typeName={this.props.typeName} />
                                    &nbsp;
                                    pr. stk
                                </div>
                            )}

                            {this.props.showTotalPrices &&
                                <>
                                <div className={`${className}__total-price__label`} data-failed={this.props.data.product.PriceFailed}>Totalt</div>
                                <div className={`${className}__total-price`} data-failed={this.props.data.product.PriceFailed}>
                                    <PriceComponent
                                        context={this.props.context}
                                        data={{ price: { CustomerContextualPrice: (price || 0) * this.state.orderQuantity } }}
                                        id={this.props.id}
                                        typeName={this.props.typeName} />
                                </div>
                                </>
                            }
                            </>
                        )}
                    </div>
                </div>

                {isAuthenticated && (
                    <div className={`${className}__right`}>
                        <div className={classnames(`${className}__quantity`)}>
                            {this._getSearchResultOrderQuantity(readOnly)}
                        </div>
                        {actionsAvailable && (
                            <div className={`${className}__actions`}>
                                {this.props.showCartCount && this._renderCartCount(className)}
                                {actionsAvailable?.addToCartAction && (
                                    <>
                                        {!this.props.orderTemplateViewProps && this._getCartButton()}
                                        {this.props.orderTemplateViewProps && this._getOrderTemplateBuyButton()}
                                    </>
                                )}
                                {actionsAvailable?.productComparisonButton && this.props.productComparisonButton && this.renderProductComparisonButton(
                                    this.props.productComparisonButton,
                                    product,
                                    getCatalogId(this.props.context.request))}
                            </div>
                        )}
                    </div>
                )}

                {isAuthenticated && ((actionsAvailable && Object.values(actionsAvailable).some(Boolean)) || draggable) && (
                    <div className={`${className}__line-actions`}>
                        {actionsAvailable?.removeAction && this._getRemoveActionButton(
                            'btn-remove',
                            this.props.resources.removeButtonText || 'Fjern',
                            this._removeItem)}

                        {draggable && (
                            <div className={`${className}__line-actions__move`}>
                                <button className='btn btn-move-up' onClick={() => this.props.onMove?.(product.RecordId, 'up')} />
                                <button className='btn btn-move-down' onClick={() => this.props.onMove?.(product.RecordId, 'down')} />
                            </div>
                        )}
                    </div>
                )}
            </li>
        );
    }

    private readonly _handleDragStart = (e: React.DragEvent<HTMLDivElement>) => {
        e.stopPropagation();

        const { product } = this.props.data;
        const offsetX = e.clientX - (e.target as Element).getBoundingClientRect().left;
        const offsetY = e.clientY - (e.target as Element).getBoundingClientRect().top;

        e.dataTransfer.setData('product', JSON.stringify({
            ProductName: product.Name || '',
            ProductRecordId: product.RecordId || 0,
            ProductUnitOfMeasure: product.DefaultUnitOfMeasure || ''
        }));
        e.dataTransfer.setDragImage((e.target as Element).parentElement as Element, offsetX, offsetY);
        e.dataTransfer.effectAllowed = 'copyMove';
    };

    private _renderPrice = (className: string, price: number | undefined): React.ReactNode => {
        if (!this.props.context.request.user.isAuthenticated) {
            return undefined;
        }

        if (this.props.data.product.PriceFailed) {
            return <span className={`${className}__price__error`}>{this.props.resources.priceErrorText || 'Pris n/a'}</span>;
        }

        if (!price) {
            return <span className={`${className}__price__retrieving`}>-</span>;
        }

        const basePrice = this.props.data.product.BasePrice;

        return (
            <PriceComponent
                context={this.props.context}
                data={{ price: this.props.data.product.ProductPrice || { CustomerContextualPrice: price, BasePrice: basePrice } }}
                id={this.props.id}
                typeName={this.props.typeName}
                discountPercentageSide='left' />
        );
    };

    private _renderCartCount = (baseClassName: string): React.ReactNode => {
        const { cart, product } = this.props.data;
        // const cartQuantity = cart?.cart.CartLines?.find(x => x.ProductId === product.RecordId)?.Quantity;
        const cartQuantity = cart?.cart.CartLines?.filter(x => x.ProductId === product.RecordId).reduce((acc, val) => acc + (val.Quantity || 0), 0);

        if (!cartQuantity) {
            return undefined;
        }

        const cartCountId = `cart-count-${product.RecordId}`;
        const tooltipString = this.props.resources.inCartTooltipText || '{0} i handlekurven';
        const tooltipText = format(tooltipString, cartQuantity);

        return (
            <div
                id={cartCountId}
                className={`${baseClassName}__cart-count`}
                onMouseOver={() => this.setState({ showCartCountTooltip: true })}
                onMouseOut={() => this.setState({ showCartCountTooltip: false })}
                onTouchStart={() => this.setState({ showCartCountTooltip: true })}
            >
                {cartQuantity}
                <Tooltip
                    isOpen={this.state.showCartCountTooltip}
                    target={`#${cartCountId}`}
                    className='msc-cart-count-tooltip'
                >
                    {tooltipText}
                </Tooltip>
            </div>
        );
    };

    private _getAvailabilityClassName = (): 'hidden' | 'retrieving' | 'available' | 'error' | 'not-available' => {
        const { product } = this.props.data;
        const { showAvailability } = this.props;

        if (!showAvailability) {
            return 'hidden';
        }

        if (!product.Availability) {
            return 'retrieving';
        }

        if (product.Availability.IsProductAvailable) {
            return 'available';
        }

        if (product.Availability.StockLevelCode === 'ERR') {
            return 'error';
        }

        return 'not-available';
    };

    private _getOrderTemplateBuyButton = (): React.ReactNode => {
        const { working } = this.state;
        const text = this.locale.cartButton.buy;
        const disabled = this.props.disableBuyButton || working || this.state.orderQuantity < 1;
        const clickHandler = async () => {
            await this._addItemToCart(undefined, true);
        };

        return (
            <Button aria-label={text} className='btn-cart-button' title={text} disabled={disabled} onClick={clickHandler}>
                <span className='fa-regular fa-cart-shopping shop-cart-icon' />
                {text}
            </Button>
        );
    };

    private _getCartButton = (): React.ReactNode => {
        const { data: { product }, cartQueue, disableBuyButton } = this.props;
        const { orderQuantity, working } = this.state;

        const className = classnames('btn-cart-button', { working });
        const icon = working ? null : <span className='fa-regular fa-cart-shopping shop-cart-icon' />;
        const disabled = disableBuyButton || working;
        const clickHandler = (action: 'buy' | 'buy_always' | 'remove' | 'update') => {
            return async () => {
                this.setState({ working: true });
                await cartQueue.add(async () => {
                    switch (action) {
                        case 'buy':
                            await this._addItemToCart(undefined);
                            break;
                        case 'buy_always':
                            await this._addItemToCartAlways();
                            break;
                        case 'remove':
                            await this._removeItemFromCart();
                            break;
                        case 'update':
                            await this._addItemToCart(product.CartLine?.LineId);
                            break;
                    }
                });
                this.setState({ working: false });
            };
        };
        const baseProps: ButtonProps = {
            className,
            disabled
        };

        if (this.props.buyAlwaysAdd) {
            const text = this.locale.cartButton.buy;
            return (
                <Button {...baseProps} aria-label={text} onClick={clickHandler('buy_always')} title={text}>
                    {icon}{text}
                </Button>
            );
        } else {
            if (!product.CartLine || !product.CartLine.Quantity) {
                const text = this.locale.cartButton.buy;
                return (
                    <Button {...baseProps} aria-label={text} onClick={clickHandler('buy')} title={text}>
                        {icon}{text}
                    </Button>
                );
            }

            if (product.CartLine.Quantity > 0 && orderQuantity < 1) {
                const text = this.locale.cartButton.remove;
                return (
                    <Button {...baseProps} aria-label={text} onClick={clickHandler('remove')} title={text}>
                        {icon}{text}
                    </Button>
                );
            }

            if (product.CartLine.Quantity !== orderQuantity) {
                const text = this.locale.cartButton.update;
                return (
                    <Button {...baseProps} aria-label={text} onClick={clickHandler('update')} title={text}>
                        {icon}{text}
                    </Button>
                );
            }
        }

        return (
            <Button aria-label={this.locale.cartButton.disabledTooltip} className={className} title={this.locale.cartButton.disabledTooltip} disabled>
                {icon}{this.locale.cartButton.buy}
            </Button>
        );
    };

    private readonly _getSearchResultOrderQuantity = (readOnly?: boolean): React.ReactNode => {
        const { product, simpleProduct } = this.props.data;

        if (readOnly) {
            return (
                <div className='quantity readonly'>
                    <span className='quantity-input'>
                        {this.state.orderQuantity}
                        &nbsp;
                        <UnitOfMeasure context={this.props.context} unitOfMeasure={simpleProduct?.DefaultUnitOfMeasure || product.DefaultUnitOfMeasure} />
                    </span>
                </div>
            );
        }

        return (
            <ProductListOrderQuantity
                context={this.props.context}
                allowZero={this.props.moduleType !== 'dlvry-cart'}
                product={this.props.data.simpleProduct
                    ? {
                        ...this.props.data.simpleProduct,
                        DefaultUnitOfMeasure: simpleProduct?.DefaultUnitOfMeasure || product.DefaultUnitOfMeasure
                    }
                    : undefined
                }
                unitConversions={this.props.data.unitConversions}
                locale={this.props.context.request.locale}
                currentCount={this.state.orderQuantity}
                onChange={this._onUpdateQuantity}
                breakoff={this.props.initialBreakoffEnabled}
                onBreakoffChange={enabled => this.props.onBreakoffChange?.(this.props.data.product, enabled)}
                inputQuantityAriaLabel=''
                incrementButtonAriaLabel=''
                decrementButtonAriaLabel=''
                disabled={this.props.disableQuantityInput ||  !this.props.data.simpleProduct}
                min={this.props.min || 0}
            />
        );
    };

    private readonly _onUpdateQuantity = (newQuantity: number) => {
        const {
            cartViewProps,
            moduleType,
            selectOnQuantityChange,
            isSelected,
            onSelectCartHandler,
            onUpdateQuantity,
            data: {
                product
            }
        } = this.props;

        this.setState({ orderQuantity: newQuantity });

        if (cartViewProps && cartViewProps.cartlines && moduleType === 'dlvry-cart') {
            const line: any = cartViewProps.cartlines.find(cartLine => cartLine?.data?.product?.RecordId === product.RecordId);
            if (line && line.cartline && line.cartline.props.quantityOnChange) {
                onUpdateQuantity?.(newQuantity);
                return line.cartline.props.quantityOnChange(line.data.cartline, newQuantity);
            }
        }

        if (selectOnQuantityChange) {
            if (this.props.buyAlwaysAdd && newQuantity === 0) {
                onSelectCartHandler?.();
            } else if (!this.props.buyAlwaysAdd && (product.CurrentQuantity || 0) === newQuantity && isSelected) {
                onSelectCartHandler?.();
            } else if (!isSelected) {
                onSelectCartHandler?.();
            }
        }
        onUpdateQuantity?.(newQuantity);
    };

    private readonly _renderProductImage = (
        className: string,
        context: ICoreContext,
        heading: string,
        image?: string,
        fallbackImage?: string,
        lazyLoadImages?: boolean
    ): JSX.Element | null => {
        const defaultImageSettings: IImageSettings = {
            viewports: {
                xs: { w: 64, h: 64, q: 'w=64&h=64&q=80&m=0&f=webp', qe: 't=64h64qhigh' },
                lg: { w: 64, h: 64, q: 'w=64&h=64&q=80&m=0&f=webp', qe: 't=64h64qhigh' },
                xl: { w: 64, h: 64, q: 'w=64&h=64&q=80&m=0&f=webp', qe: 't=64h64qhigh' }
            },
            disableLazyLoad: !Boolean(lazyLoadImages),
            lazyload: Boolean(lazyLoadImages),
            quality: 80
        };

        const fallImageUrl = getFallbackImageUrl(this.props.data.product.ItemId, context.request.apiSettings, context.request.app?.config?.OmniChannelMedia);

        return (
            <Image
                requestContext={context.actionContext.requestContext}
                className={className}
                altText={heading}
                title={heading}
                src={image}
                fallBackSrc={fallbackImage || fallImageUrl}
                gridSettings={context.request.gridSettings!}
                imageSettings={defaultImageSettings}
                loadFailureBehavior='empty'
                fetchPriority={ImageFetchPriority.High}
                tabIndex={0}
            />
        );
    };

    // @ts-ignore
    private readonly _getAddToCartActionButton = (className: string, buttonText: string, clickHandler: (event: React.MouseEvent<HTMLElement>) => void): React.ReactNode => {
        return (
            <Button aria-label={buttonText} className={className} onClick={clickHandler} title={buttonText}>
                <span className={`${className}__text`}>{buttonText}</span>
                <span className='fa-regular fa-cart-shopping' />
            </Button>
        );
    };

    private readonly _getRemoveActionButton = (className: string, buttonText: string, clickHandler: (event: React.MouseEvent<HTMLElement>) => void): React.ReactNode => {
        return (
            <Button aria-label={buttonText} className={className} onClick={clickHandler} title={buttonText}>
                <span className='fa-regular fa-xmark no-margin' />
            </Button>
        );
    };

    private readonly _getWishlistActionButton = (): React.ReactNode => {
        const { resources } = this.props;
        const { product, wishlists } = this.props.data;
        const isWishlisted = wishlists?.[0]?.CommerceListLines?.some(x => x.ProductId === product.RecordId);
        const buttonText = isWishlisted
            ? resources.removeFromWishlistText || 'Fjern fra huskelisten'
            : resources.addToWishlistText || 'Legg til i huskelisten';
        const onClick = isWishlisted ? this.removeItemFromWishlist : this.moveItemToWishlist;

        return (
            <Button
                className={classnames('btn-add-to-wishlist', { 'wishlisted': isWishlisted })}
                title={buttonText}
                aria-label={buttonText}
                onClick={onClick} />
        );
    };

    private readonly _removeItem = (): void => {
        if (this.props.cartViewProps &&
            this.props.cartViewProps.removeItemClickHandler &&
            this.props.cartViewProps.cartlines) {
            const line =
                this.props.cartViewProps.cartlines.find(cartLine => cartLine?.data?.product?.RecordId ===
                    this.props.data.product.RecordId);
            if (line && line.data) {
                this.props.cartViewProps.removeItemClickHandler(line.data.cartline);
            }
        } else if (this.props.wishlistItemsProps) {
            const wishlists = this.props.wishlistItemsProps?.data.wishlists;
            const wishlistLineId = this.getWishlistLineIdFromProductId(this.props.data.product.RecordId);
            if (wishlists.result && wishlists.result[0] && wishlistLineId) {
                removeLinesAsync({ callerContext: this.props.context.actionContext },
                        wishlists.result[0].Id,
                        [{ LineId: wishlistLineId }])
                    .then(commerceList => {
                        window.location.reload();
                    })
                    .catch(error => {
                        const footer = error?.data?.CorrelationId ? `Correlation Id: ${error?.data?.CorrelationId}` : undefined;
                        toasts.error(this.locale.toastStrings.errorHeader, error, { footer });
                    });
            }
        } else if (this.props.orderTemplateViewProps) {
            const { context, data: { product, orderTemplate } } = this.props;
            const { telemetry } = this.props.orderTemplateViewProps;

            if (context && context.actionContext) {
                const maxWaitTimeInMilliSeconds = 1000;
                const line = orderTemplate?.orderTemplateLines.find(x => x.productListLine.ProductId === product.RecordId);
                const userToken = context.request.user.isAuthenticated && context.request.user.token;

                if (userToken && orderTemplate && line) {
                    // Quantity needs to be at least 1 for some reason
                    line.productListLine.Quantity = line.productListLine.Quantity || 1;

                    removeProductListLinesAsync({ callerContext: context.actionContext }, line.productListLine.ProductListId!, [line.productListLine])
                        .then(async () => {
                            // eslint-disable-next-line no-promise-executor-return -- fix
                            await new Promise(resolve => setTimeout(resolve, maxWaitTimeInMilliSeconds));

                            orderTemplate.orderTemplateLines.splice(orderTemplate.orderTemplateLines.findIndex(x => x.productListLine.ProductId === product.RecordId), 1);
                            orderTemplate.productList.ProductListLines!.splice(orderTemplate.productList.ProductListLines!.findIndex(x => x.ProductId === product.RecordId), 1);
                            orderTemplate.totalLines--;

                            this.props.orderTemplateViewProps!.onUpdateQuantity?.({
                                RecordId: line?.fullProduct?.ProductDetails.RecordId
                            } as any, 0);

                            context.actionContext.update(new ActiveOrderTemplateLinesInput(context.request, this.props.config as any), orderTemplate);
                        })
                        .catch(error => {
                            telemetry.warning('[order-template-lines] Unable remove items from the order template lines');
                            telemetry.error(error);
                        });
                } else {
                    telemetry.warning(
                        '[order-template-lines] Unable remove items from the order template lines user token or lineItemID was not found'
                    );
                }
            }
        }
    };

    private readonly getWishlistLineIdFromProductId = (productId: number | undefined): number | null => {
        const wishlists = this.props.wishlistItemsProps?.data.wishlists;

        if (!wishlists ||
            !wishlists.result ||
            !productId ||
            wishlists.result.length === 0 ||
            !wishlists.result[0].CommerceListLines ||
            wishlists.result[0].CommerceListLines.length === 0) {
            return null;
        }

        const foundWishlistItem = wishlists.result[0].CommerceListLines.filter(commerceListLine => {
            return commerceListLine.ProductId === productId;
        });

        if (foundWishlistItem && foundWishlistItem[0].LineId) {
            return foundWishlistItem[0].LineId;
        }
        return null;
    };

    private readonly moveItemToWishlist = async(): Promise<void> => {
        const { context } = this.props;
        const { product } = this.props.data;

        if (!context || !product || !context.request.user.catalogId) {
            return Promise.resolve();
        }

        const productInputs: ProductInput[] = [];
        const productInput = new ProductInput(
            product.RecordId!,
            context.request.apiSettings,
            undefined, undefined,
            context.request,
            +context.request.user.catalogId
        );
        productInputs.push(productInput);

        if (context.request.user.isAuthenticated) {
            const customer = await this.getCustomerAccount();

            let wishlists = this.props.data.wishlists;

            if (!wishlists) {
                wishlists = await getWishlistByCustomerId(createGetByCustomerInput({}, null), context.actionContext);
            }

            if (!wishlists || wishlists.length === 0) {
                const wishlistName = this.props.defaultWishlistName || 'My wishlist';
                if (customer && customer.AccountNumber) {
                    const listData: CommerceList = {
                        CustomerId: customer.AccountNumber,
                        Name: wishlistName,
                        CommerceListTypeValue: 1,
                        Id: 0
                    };
                    try {
                        const commerceList = await createCommerceListAsync({
                                callerContext: this.props.context.actionContext
                            },
                            listData);
                        await this.callAddWishlistLine(product.RecordId!, commerceList.Id);
                    } catch (err) {
                        const error = err as unknown as any;
                        const footer = error?.data?.CorrelationId ? `Correlation Id: ${error?.data?.CorrelationId}` : undefined;
                        toasts.error(this.locale.toastStrings.errorHeader, error, { footer });
                    }
                }
            } else {
                await this.callAddWishlistLine(product.RecordId!, wishlists[0].Id);

                toast({
                    text: `${product.Name} lagt i huskelisten`,
                    type: 'success',
                    autoClose: 3000,
                    position: 'bottom-right',
                    canClose: true,
                    showProgress: false
                });
            }
        }
    };

    private readonly removeItemFromWishlist = async(): Promise<void> => {
        const { context } = this.props;
        const { product } = this.props.data;

        if (!context || !product || !context.request.user.catalogId || !context.request.user.isAuthenticated) {
            return Promise.resolve();
        }

        let wishlists = this.props.data.wishlists;

        if (!wishlists) {
            wishlists = await getWishlistByCustomerId(createGetByCustomerInput({}, null), context.actionContext);
        }

        if (wishlists.length > 0) {
            const commerceLineToRemove = wishlists[0].CommerceListLines?.find(x => x.ProductId === product.RecordId);
            const updatedCommerceList = await removeLinesAsync({ callerContext: context.actionContext },
                wishlists[0].Id,
                [commerceLineToRemove!]);
            context.actionContext.update(createGetByCustomerInput({}, null), [updatedCommerceList]);

            toast({
                text: `${product.Name} fjernet fra huskelisten`,
                type: "success",
                autoClose: 3000,
                position: "bottom-right",
                canClose: true,
                showProgress: false
            });
        }
    };

    private readonly callAddWishlistLine = async (productId: number, wishlistId: number): Promise<void> => {
        const { context } = this.props;

        try {
            const customer = await this.getCustomerAccount();
            if (customer && customer.AccountNumber) {
                const createdCommerceList = await addLinesAsync({ callerContext: context.actionContext },
                    wishlistId,
                    [
                        buildWishlistLine(productId, customer.AccountNumber, wishlistId)
                    ]);
                context.actionContext.update(createGetByCustomerInput({}, null), [createdCommerceList]);
            }
        } catch (err) {
            const error = err as unknown as any;
            const footer = error?.data?.CorrelationId ? `Correlation Id: ${error?.data?.CorrelationId}` : undefined;
            toasts.error(this.locale.toastStrings.errorHeader, error, { footer });
        }
    };

    private readonly getCustomerAccount = (): AsyncResult<Customer> => {
        const customerInput = new GetCustomerInput(this.props.context.actionContext.requestContext.apiSettings);
        return getCustomer(customerInput, this.props.context.actionContext);
    };

    private readonly _addItemToCartAlways = async (): Promise<void> => {
        const { context } = this.props;
        const { product, simpleProduct } = this.props.data;

        if (!context || !product || !simpleProduct || !context.request.user.catalogId) {
            return Promise.resolve();
        }

        let cart = await getCartStateData(new GetCartStateDataInput(context.request.apiSettings, context.request), context.actionContext);

        if (!cart) {
            return;
        }

        while (cart.status === 'UPDATING') {
            console.log('Cart is updating, waiting for it to finish...');
            await new Promise((resolve) => setTimeout(resolve, 1000));
            cart = await getCartStateData(new GetCartStateDataInput(context.request.apiSettings, context.request), context.actionContext);

            if (!cart) {
                // Shouldn't happen, but just in case
                toast({
                    header: 'Error',
                    text: 'Cart is undefined. Please try again later.',
                    autoClose: 30000,
                    canClose: true,
                    position: 'bottom-right',
                    showProgress: true
                });
                return Promise.resolve();
            }
        }

        let userHasAccess: boolean | undefined = this.props.userHasAccess || true; // TODO: Remove 'true' default here to re-enable alcohol license check
        let validationError: string | undefined;

        if (userHasAccess === undefined) {
            const result = await validateAlcoholLicenseAsync(context.actionContext, {
                CatalogId: context.request.apiSettings.catalogId,
                ProductIds: [simpleProduct.RecordId]
            });
            userHasAccess = result.ValidationResult;
            validationError = this.locale.toastStrings.noAccessToProduct;
        }

        const quantity = this.state.orderQuantity || (this.props.initialBreakoffEnabled
            ? 1
            : simpleProduct.Behavior?.MultipleOfQuantity || 1);

        if (userHasAccess && this.props.onBuyValidate) {
            const result = await this.props.onBuyValidate(
                product,
                quantity
            );
            userHasAccess = result.isValid;
            validationError = result.message;
        }

        if (userHasAccess !== true) {
            toasts.error(this.locale.toastStrings.errorHeader, validationError || 'Unknown error');
            return Promise.resolve();
        }

        const addProductToCartInput = {
            product: simpleProduct,
            count: quantity,
            customPrice: product.Price
        };

        const cartLine = cart.cart.CartLines?.find(x => x.ProductId === simpleProduct.RecordId);

        try {
            const toAddCount = (!cartLine || !cartLine.LineId)
                ? addProductToCartInput.count
                : quantity;
            const result = (!cartLine || !cartLine.LineId)
                ? await cart.addProductToCart(addProductToCartInput)
                : await cart.updateCartLineQuantity({
                    cartLineId: cartLine.LineId,
                    newQuantity: (cartLine.Quantity || 0) + toAddCount
                });

            if (result.status === 'SUCCESS') {
                const msg: IToastViewProps = {
                    text: this.locale.toastStrings.productsAddedToCart
                        .replace('{0}', toAddCount.toString())
                        .replace('{1}', simpleProduct.Name ?? ''),
                    type: 'success',
                    autoClose: 3000,
                    position: 'bottom-right',
                    canClose: true,
                    showProgress: true,
                    link: this.cartUrl,
                    linkLabel: this.locale.toastStrings.goToCart
                };
                toast(msg);

                if (this.props.isSelected) {
                    this.props.onSelectCartHandler?.();
                }
                this.setState({ orderQuantity: 0 });
            }
            else if (result.status === 'FAILED') {
                const text = result.validationResults && result.validationResults.length > 0
                    ? result.validationResults[0].LocalizedMessage!
                    : (result.errorDetails?.LocalizedMessage ?? 'Unknown error adding items to cart');
                toasts.error(this.locale.toastStrings.errorHeader, text);
            }
        }
        catch (err) {
            const error = err as unknown as any;
            const footer = error?.data?.CorrelationId ? `Correlation Id: ${error?.data?.CorrelationId}` : undefined;
            toasts.error(this.locale.toastStrings.errorHeader, error, { footer });
        }
    };

    private readonly _addItemToCart = async (cartLineId: string | undefined, resetQuantiyOnSuccess?: boolean): Promise<void> => {
        const { context } = this.props;
        const { product, simpleProduct } = this.props.data;

        if (!context || !product || !simpleProduct || !context.request.user.catalogId) {
            return Promise.resolve();
        }

        let cart = await getCartStateData(new GetCartStateDataInput(context.request.apiSettings, context.request), context.actionContext);

        if (cart) {
            while (cart.status === 'UPDATING') {
                console.log('Cart is updating, waiting for it to finish...');
                await new Promise((resolve) => setTimeout(resolve, 1000));
                cart = await getCartStateData(new GetCartStateDataInput(context.request.apiSettings, context.request), context.actionContext);

                if (!cart) {
                    // Shouldn't happen, but just in case
                    toast({
                        header: 'Error',
                        text: 'Cart is undefined. Please try again later.',
                        autoClose: 30000,
                        canClose: true,
                        position: 'bottom-right',
                        showProgress: true
                    });
                    return Promise.resolve();
                }
            }

            let userHasAccess: boolean | undefined = this.props.userHasAccess || true; // TODO: Remove 'true' default here to re-enable alcohol license check
            let validationError: string | undefined;

            if (userHasAccess === undefined) {
                const result = await validateAlcoholLicenseAsync(context.actionContext, {
                    CatalogId: context.request.apiSettings.catalogId,
                    ProductIds: [simpleProduct.RecordId]
                });
                userHasAccess = result.ValidationResult;
                validationError = this.locale.toastStrings.noAccessToProduct;
            }

            const quantity = this.state.orderQuantity || (this.props.initialBreakoffEnabled
                ? 1
                : simpleProduct.Behavior?.MultipleOfQuantity || 0);

            if (userHasAccess && this.props.onBuyValidate) {
                const result = await this.props.onBuyValidate(
                    product,
                    quantity
                );
                userHasAccess = result.isValid;
                validationError = result.message;
            }

            if (userHasAccess !== true) {
                toasts.error(this.locale.toastStrings.errorHeader, validationError || 'Unknown error');
                return Promise.resolve();
            }

            /* if (!cartLineId) {
                cartLineId = cart.cart.CartLines?.find(x => x.ProductId === simpleProduct.RecordId)?.LineId;
            }
            const cartQuantity = cartLineId ? cart.cart.CartLines?.find(x => x.LineId === cartLineId)?.Quantity || 0 : 0; */

            const addProductToCartInput = {
                product: simpleProduct,
                count: quantity,
                /* isPriceKeyedIn: true, */
                customPrice: product.Price
            };

            try {
                const result = !cartLineId
                    ? await cart.addProductToCart(addProductToCartInput)
                    : await cart.updateCartLineQuantity({ cartLineId, newQuantity: this.state.orderQuantity });
                if (result.status === "SUCCESS") {
                    const msg: IToastViewProps = {
                        text: this.locale.toastStrings.productsAddedToCart
                            .replace('{0}', quantity.toString())
                            .replace('{1}', simpleProduct.Name ?? ''),
                        type: "success",
                        autoClose: 3000,
                        position: "bottom-right",
                        canClose: true,
                        showProgress: true,
                        link: this.cartUrl,
                        linkLabel: this.locale.toastStrings.goToCart
                    };
                    toast(msg);

                    if (resetQuantiyOnSuccess) {
                        // Reset quantity to 0 after successfully adding to cart
                        this.setState({ orderQuantity: 0 });
                        this.props.onUpdateQuantity?.(0);
                    } else if (this.state.orderQuantity === 0) {
                        // Make sure product has at least 1 quantity after being successfully added to cart
                        this.setState({ orderQuantity: 1 });
                        this.props.onUpdateQuantity?.(1);
                    }
                }
                else if (result.status === "FAILED") {
                    const text = result.validationResults && result.validationResults.length > 0
                        ? result.validationResults[0].LocalizedMessage!
                        : (result.errorDetails?.LocalizedMessage ?? 'Unknown error adding items to cart');
                    toasts.error(this.locale.toastStrings.errorHeader, text);
                }
            }
            catch (err) {
                const error = err as unknown as any;
                const footer = error?.data?.CorrelationId ? `Correlation Id: ${error?.data?.CorrelationId}` : undefined;
                toasts.error(this.locale.toastStrings.errorHeader, error, { footer });
            }
        }
    };

    private readonly _removeItemFromCart = async (): Promise<void> => {
        const { context } = this.props;
        const { product } = this.props.data;

        if (!product.CartLine || !product.CartLine.LineId) {
            return Promise.resolve();
        }

        const cart = await getCartStateData(new GetCartStateDataInput(context.request.apiSettings, context.request), context.actionContext);

        if (!cart) {
            toasts.error(this.locale.toastStrings.errorHeader, this.locale.toastStrings.errorCartNotFound);
            return Promise.resolve();
        }

        const result = await cart.removeCartLines({
            cartLineIds: [product.CartLine.LineId]
        });

        if (result.status === "SUCCESS") {
            toast({
                text: this.locale.toastStrings.productsRemovedFromCart.replace('{0}', product.Name ?? ''),
                type: "success",
                autoClose: 3000,
                position: "bottom-right",
                canClose: true,
                showProgress: true,
                link: this.cartUrl,
                linkLabel: this.locale.toastStrings.goToCart
            });
        } else if (result.status === "FAILED") {
            const text = result.validationResults && result.validationResults.length > 0
                ? result.validationResults[0].LocalizedMessage!
                : (result.errorDetails?.LocalizedMessage ?? 'Unknown error removing items from cart');
            toasts.error(this.locale.toastStrings.errorHeader, text);
        }
    };
}
